import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  Cake,
  Lock,
  LogIn,
  Mail,
  MailCheck,
  Phone,
  Trophy,
  User,
  UserPlus,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useState, type CSSProperties, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes } from 'react';
import type { InvitePreview, StartRegistrationResponse } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button } from '../components/ui';
import { AuthInput, AuthLabel } from '../components/auth-ui';

type AccountType = 'player' | 'parent';

/** Same visual language as `AuthInput` but for a `<select>` — team/training
 * group pickers on both forms. */
function AuthSelect({
  className = '',
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      className={`w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none transition focus:border-brand-accent focus:bg-white focus:ring-2 focus:ring-brand-accent/20 ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

/** Trainingsgruppen-Auswahl für Spieler- oder Kind-Formular — nur dann als
 * Pflichtfeld gerendert, wenn der Verein überhaupt eine aktive Gruppe
 * hinterlegt hat (`preview.trainingGroups`). Ohne das würde ein Verein, der
 * noch keine Trainingsgruppe angelegt hat, jede Selbst-Registrierung
 * blockieren — ein leeres, aber als `required` markiertes `<select>` lässt
 * sich gar nicht absenden. `InvitesService.assertTrainingGroupProvided()`
 * erzwingt dieselbe Bedingung serverseitig. */
function TrainingGroupField({
  groups,
  value,
  onChange,
}: {
  groups: { id: string; name: string }[];
  value: string;
  onChange: (value: string) => void;
}) {
  if (groups.length === 0) {
    return (
      <div>
        <AuthLabel>Trainingsgruppe</AuthLabel>
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5 text-xs text-slate-500">
          Der Verein hat noch keine Trainingsgruppe hinterlegt — das lässt sich später nachholen.
        </p>
      </div>
    );
  }
  return (
    <div>
      <AuthLabel>Trainingsgruppe</AuthLabel>
      <AuthSelect value={value} onChange={(e) => onChange(e.target.value)} required>
        <option value="">— bitte wählen —</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </AuthSelect>
    </div>
  );
}

function LabeledInput({
  label,
  icon,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; icon: LucideIcon }) {
  return (
    <div>
      <AuthLabel>{label}</AuthLabel>
      <AuthInput icon={icon} {...props} />
    </div>
  );
}

/** Big, tappable card for the account-type choice — the very first thing a
 * visitor sees on the registration page. */
function AccountTypeCard({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: typeof User;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-brand-accent hover:shadow-md sm:p-5"
    >
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-accent to-indigo-600 text-white shadow-md shadow-brand-accent/20 transition group-hover:scale-105">
        <Icon size={22} />
      </div>
      <div>
        <div className="font-semibold text-slate-900">{title}</div>
        <div className="text-sm text-slate-500">{description}</div>
      </div>
    </button>
  );
}

export function InvitePage() {
  const { token } = useParams({ strict: false });
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const { data: preview, isLoading, isError } = useQuery({
    queryKey: ['invite', token],
    queryFn: () => api<InvitePreview>(`/public/invites/${token}`),
    retry: false,
  });

  const [accountType, setAccountType] = useState<AccountType | null>(null);
  // 'join' = the email already has an account elsewhere on the platform
  // (register came back 409 email_taken) — the password field then
  // authenticates that existing account instead of setting a new one.
  const [mode, setMode] = useState<'register' | 'join'>('register');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [teamId, setTeamId] = useState('');
  const [trainingGroupId, setTrainingGroupId] = useState('');

  const [childFirstName, setChildFirstName] = useState('');
  const [childLastName, setChildLastName] = useState('');
  const [childBirthDate, setChildBirthDate] = useState('');
  const [childTeamId, setChildTeamId] = useState('');
  const [childTrainingGroupId, setChildTrainingGroupId] = useState('');

  function buildBody() {
    if (accountType === 'parent') {
      return {
        accountType: 'parent' as const,
        firstName,
        lastName,
        email,
        password,
        emergencyPhone: emergencyPhone || undefined,
        child: {
          firstName: childFirstName,
          lastName: childLastName,
          birthDate: childBirthDate,
          teamId: childTeamId || undefined,
          trainingGroupId: childTrainingGroupId || undefined,
        },
      };
    }
    return {
      accountType: 'player' as const,
      firstName,
      lastName,
      email,
      password,
      birthDate,
      emergencyPhone: emergencyPhone || undefined,
      teamId: teamId || undefined,
      trainingGroupId: trainingGroupId || undefined,
    };
  }

  // Registrieren legt noch kein Konto an — erst nach Klick auf den
  // Bestätigungslink aus der E-Mail (siehe docs/members.md,
  // "E-Mail-Bestätigung"). Bis dahin zeigt die Seite nur eine
  // "Prüfe dein Postfach"-Bestätigung statt sofort einzuloggen.
  const [confirmationSentTo, setConfirmationSentTo] = useState<string | null>(null);
  const register = useMutation({
    mutationFn: async () => {
      return api<StartRegistrationResponse>(`/public/invites/${token}/register`, {
        method: 'POST',
        body: buildBody(),
      });
    },
    onSuccess: (res) => {
      setConfirmationSentTo(res.email);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.problem.code === 'email_taken') {
        setMode('join');
        setPassword('');
      }
    },
  });

  const join = useMutation({
    mutationFn: async () => {
      await api(`/public/invites/${token}/join`, { method: 'POST', body: buildBody() });
    },
    onSuccess: async () => {
      await refresh();
      await navigate({ to: '/' });
    },
  });

  const design = preview?.authDesign;
  const cssVars = design?.primaryColor ? ({ '--brand-accent': design.primaryColor } as CSSProperties) : undefined;
  const pageBg = design?.backgroundColor ? { backgroundColor: design.backgroundColor } : undefined;
  const logoUrl = preview?.logoFileId ? `/api/public/invites/${token}/logo` : null;
  const heroImageUrl = preview?.authImageFileId ? `/api/public/invites/${token}/auth-image` : null;

  if (isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
        <p className="text-sm text-slate-400">Einen Moment …</p>
      </div>
    );
  }

  if (isError || !preview) {
    return (
      <div className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4">
        <div className="w-full max-w-md rounded-2xl border border-white/60 bg-white/90 p-8 text-center shadow-xl">
          <h1 className="mb-3 text-xl font-semibold text-slate-900">Einladung ungültig</h1>
          <Alert>
            Dieser Einladungslink ist ungültig oder abgelaufen. Bitte beim Verein einen neuen Link
            anfordern.
          </Alert>
        </div>
      </div>
    );
  }

  if (confirmationSentTo) {
    return (
      <div
        className="flex min-h-full items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4"
        style={{ ...cssVars, ...pageBg }}
      >
        <div className="w-full max-w-md rounded-2xl border border-white/60 bg-white/95 p-8 text-center shadow-xl">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-accent to-indigo-600 text-white shadow-md shadow-brand-accent/20">
            <MailCheck size={26} />
          </div>
          <h1 className="mb-2 text-xl font-semibold text-slate-900">Bestätige deine E-Mail</h1>
          <p className="text-sm text-slate-500">
            Wir haben einen Bestätigungslink an <strong className="text-slate-700">{confirmationSentTo}</strong>{' '}
            geschickt. Klicke innerhalb von 10 Minuten darauf, um dein Konto bei {preview.organizationName}{' '}
            anzulegen — sonst verfällt die Registrierung und du musst das Formular erneut ausfüllen.
          </p>
        </div>
      </div>
    );
  }

  const isJoin = mode === 'join';
  const active = isJoin ? join : register;

  return (
    <div className="flex min-h-full flex-col md:flex-row" style={{ ...cssVars, ...pageBg }}>
      {/* Desktop: large club image left. Mobile: same image, short, on top. */}
      <div className="relative flex min-h-[200px] shrink-0 items-end overflow-hidden bg-gradient-to-br from-brand-accent to-indigo-700 md:min-h-full md:w-1/2">
        {heroImageUrl && (
          <img src={heroImageUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
        )}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.55) 100%)' }}
        />
        <div className="relative z-10 p-6 text-white sm:p-10">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="mb-4 h-14 w-14 rounded-2xl border border-white/40 bg-white object-contain p-1.5 shadow-lg"
            />
          ) : (
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 shadow-lg backdrop-blur-sm">
              <Trophy size={26} />
            </div>
          )}
          <h1 className="text-2xl font-bold sm:text-3xl">{design?.heading || preview.organizationName}</h1>
          {design?.welcomeText && (
            <p className="mt-2 max-w-sm text-sm text-white/85 sm:text-base">{design.welcomeText}</p>
          )}
        </div>
      </div>

      {/* Selection / form */}
      <div className="flex flex-1 items-center justify-center p-4 py-8 sm:p-8">
        <div className="w-full max-w-md">
          {accountType === null ? (
            <div className="space-y-4">
              <div className="text-center">
                <h2 className="text-lg font-semibold text-slate-900">Wie möchtest du dich registrieren?</h2>
                <p className="mt-1 text-sm text-slate-500">Bei {preview.organizationName}</p>
              </div>
              <AccountTypeCard
                icon={User}
                title="Spielerkonto"
                description="Ich spiele selbst im Verein."
                onClick={() => setAccountType('player')}
              />
              <AccountTypeCard
                icon={Users}
                title="Elternkonto"
                description="Ich melde mein Kind an und antworte für es."
                onClick={() => setAccountType('parent')}
              />
              <p className="text-center text-sm text-slate-500">
                Schon ein Konto?{' '}
                <a href="/login" className="font-medium text-brand-accent hover:underline">
                  Zum Login
                </a>
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-white/60 bg-white/95 p-6 shadow-xl shadow-slate-900/5 backdrop-blur-sm sm:p-8">
              <button
                type="button"
                onClick={() => {
                  setAccountType(null);
                  setMode('register');
                  register.reset();
                  join.reset();
                }}
                className="mb-4 flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600"
              >
                <ArrowLeft size={14} /> Andere Kontoart wählen
              </button>

              <h2 className="mb-1 text-lg font-semibold text-slate-900">
                {isJoin
                  ? `Bei ${preview.organizationName} anmelden`
                  : accountType === 'parent'
                    ? 'Elternkonto erstellen'
                    : 'Spielerkonto erstellen'}
              </h2>
              <p className="mb-5 text-sm text-slate-500">
                {isJoin
                  ? 'Melde dich mit deinem bestehenden Passwort an, um zusätzlich hier Mitglied zu werden.'
                  : accountType === 'parent'
                    ? 'Dein Konto verwaltet die Anmeldungen deines Kindes.'
                    : 'Erstelle dein Konto, um Mitglied zu werden.'}
              </p>

              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  active.mutate();
                }}
              >
                {active.isError && (
                  <Alert>
                    {active.error instanceof ApiError ? active.error.message : 'Das hat leider nicht funktioniert.'}
                  </Alert>
                )}

                <Section title={accountType === 'parent' ? 'Elterndaten' : undefined}>
                  <div className="grid grid-cols-2 gap-3">
                    <LabeledInput
                      label="Vorname"
                      icon={User}
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                    />
                    <LabeledInput
                      label="Nachname"
                      icon={User}
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <AuthLabel>E-Mail</AuthLabel>
                    <AuthInput
                      icon={Mail}
                      type="email"
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="du@example.com"
                      required
                      disabled={isJoin}
                    />
                    {isJoin && (
                      <button
                        type="button"
                        onClick={() => {
                          setMode('register');
                          setPassword('');
                          register.reset();
                        }}
                        className="mt-1 text-xs font-medium text-brand-accent hover:underline"
                      >
                        Andere E-Mail verwenden
                      </button>
                    )}
                  </div>
                  <div>
                    <AuthLabel>{isJoin ? 'Dein bestehendes Passwort' : 'Passwort'}</AuthLabel>
                    <AuthInput
                      icon={Lock}
                      type="password"
                      autoComplete={isJoin ? 'current-password' : 'new-password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={isJoin ? '••••••••' : 'mind. 10 Zeichen'}
                      minLength={isJoin ? undefined : 10}
                      required
                    />
                  </div>
                  {accountType === 'player' && (
                    <LabeledInput
                      label="Geburtstag"
                      icon={Cake}
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      required
                    />
                  )}
                  <LabeledInput
                    label="Telefon für Notfälle (optional)"
                    icon={Phone}
                    type="tel"
                    value={emergencyPhone}
                    onChange={(e) => setEmergencyPhone(e.target.value)}
                  />
                  {accountType === 'player' && (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <AuthLabel>Mannschaft (optional)</AuthLabel>
                        <AuthSelect value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                          <option value="">— später zuordnen —</option>
                          {preview.teams.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </AuthSelect>
                      </div>
                      <TrainingGroupField
                        groups={preview.trainingGroups}
                        value={trainingGroupId}
                        onChange={setTrainingGroupId}
                      />
                    </div>
                  )}
                </Section>

                {accountType === 'parent' && (
                  <Section title="Daten zum Kind">
                    <div className="grid grid-cols-2 gap-3">
                      <LabeledInput
                        label="Vorname"
                        icon={User}
                        value={childFirstName}
                        onChange={(e) => setChildFirstName(e.target.value)}
                        required
                      />
                      <LabeledInput
                        label="Nachname"
                        icon={User}
                        value={childLastName}
                        onChange={(e) => setChildLastName(e.target.value)}
                        required
                      />
                    </div>
                    <LabeledInput
                      label="Geburtstag"
                      icon={Cake}
                      type="date"
                      value={childBirthDate}
                      onChange={(e) => setChildBirthDate(e.target.value)}
                      required
                    />
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <div>
                        <AuthLabel>Mannschaft (optional)</AuthLabel>
                        <AuthSelect value={childTeamId} onChange={(e) => setChildTeamId(e.target.value)}>
                          <option value="">— später zuordnen —</option>
                          {preview.teams.map((t) => (
                            <option key={t.id} value={t.id}>
                              {t.name}
                            </option>
                          ))}
                        </AuthSelect>
                      </div>
                      <TrainingGroupField
                        groups={preview.trainingGroups}
                        value={childTrainingGroupId}
                        onChange={setChildTrainingGroupId}
                      />
                    </div>
                  </Section>
                )}

                <Button type="submit" disabled={active.isPending} className="w-full gap-2 py-2.5">
                  {isJoin ? (
                    join.isPending ? (
                      'Wird beigetreten…'
                    ) : (
                      <>
                        <LogIn size={16} /> Anmelden und beitreten
                      </>
                    )
                  ) : register.isPending ? (
                    'Konto wird erstellt…'
                  ) : (
                    <>
                      <UserPlus size={16} /> Konto erstellen
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="space-y-3">
      {title && (
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-400">{title}</h3>
      )}
      {children}
    </div>
  );
}
